import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';
import Toast from '../components/Toast';

/**
 * AdminWithdrawals — /admin/withdrawals
 * Queue of crypto payouts. Admin approves → NowPayments payout; rejects → Points refund.
 * Shows a read-only NowPayments status card at the top.
 */
const STATUS_TABS = [
  { key: 'pending',  label: 'Pending' },
  { key: 'sent',     label: 'Sent' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'failed',   label: 'Failed' },
  { key: 'all',      label: 'All' },
];

export default function AdminWithdrawals() {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [np, setNp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // Manual-send flow (Option B — admin sends from own wallet, records tx hash)
  const [manualId, setManualId] = useState(null);
  const [manualTxHash, setManualTxHash] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get('/admin/withdrawals', { params: { status, limit: 200 } });
      setRows(data.withdrawals || []);
      setCounts(data.counts || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load withdrawals');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    api.get('/admin/nowpayments/status').then(({ data }) => setNp(data)).catch(() => {});
  }, []);

  const approve = async (w) => {
    if (busyId) return;
    setBusyId(w.id);
    try {
      await api.post(`/admin/withdrawals/${w.id}/approve`);
      setToast({ msg: `Approved ${w.usd_amount} pts payout to ${w.user.email}` });
      fetchList();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Approve failed' });
    } finally { setBusyId(null); }
  };

  const openReject = (w) => {
    setRejectingId(w.id);
    setRejectReason('');
  };
  const cancelReject = () => { setRejectingId(null); setRejectReason(''); };

  const openManual = (w) => {
    setManualId(w.id);
    setManualTxHash('');
  };
  const cancelManual = () => { setManualId(null); setManualTxHash(''); };

  const confirmManual = async (w) => {
    if (busyId) return;
    setBusyId(w.id);
    try {
      await api.post(`/admin/withdrawals/${w.id}/mark-sent-manual`, { tx_hash: manualTxHash.trim() });
      setToast({ msg: `✓ Marked as sent — ${w.usd_amount.toFixed(2)} pts to ${w.user.email}` });
      cancelManual();
      fetchList();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Mark-sent failed' });
    } finally { setBusyId(null); }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ msg: `📋 ${label} copied` });
    } catch {
      setToast({ msg: 'Copy failed — select and copy manually' });
    }
  };

  const confirmReject = async (w) => {
    if (busyId) return;
    setBusyId(w.id);
    try {
      const params = rejectReason.trim() ? { reason: rejectReason.trim() } : {};
      await api.post(`/admin/withdrawals/${w.id}/reject`, null, { params });
      setToast({ msg: `Rejected — ${w.usd_amount} pts refunded to ${w.user.email}` });
      cancelReject();
      fetchList();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Reject failed' });
    } finally { setBusyId(null); }
  };

  return (
    <AdminLayout active="withdrawals">
      <div className="admin-page" data-testid="admin-withdrawals-page">
        <header className="admin-page-header">
          <h1 className="admin-page-title">Withdrawals</h1>
          <p className="admin-page-sub">
            Every crypto payout requires your approval. Points are locked at submission and refunded on reject.
          </p>
        </header>

        {/* NowPayments status card */}
        {np && (
          <div className={`np-status-card np-${np.mode}`} data-testid="admin-np-status">
            <div className="np-status-head">
              <div>
                <div className="np-status-mode-label">NowPayments</div>
                <div className="np-status-mode-value">
                  {np.mode === 'live' ? '● LIVE' : '● STUB (sandbox)'}
                </div>
              </div>
              <div className="np-status-badge">
                {np.mode === 'live' ? 'Real payouts' : 'Fake tx hashes'}
              </div>
            </div>
            <div className="np-status-grid">
              <NpKey label="API key"    set={np.api_key_set}    value={np.api_key_masked} />
              <NpKey label="IPN secret" set={np.ipn_secret_set} value={np.ipn_secret_masked} />
              <NpKey label="Public key" set={np.public_key_set} value={np.public_key_masked} />
              <NpKey label="Webhook"    set={!!np.webhook_url}  value={np.webhook_url} mono={false} />
            </div>
            <div className="np-status-note">
              {np.note}
            </div>
          </div>
        )}

        {/* Status tabs with counts */}
        <div className="admin-toolbar">
          <div className="admin-chip-row">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`admin-chip${status === t.key ? ' active' : ''}`}
                onClick={() => setStatus(t.key)}
                data-testid={`wd-tab-${t.key}`}
              >
                {t.label}
                {counts[t.key] !== undefined && t.key !== 'all' && (
                  <span className="admin-chip-count">{counts[t.key]}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="admin-error">{err}</div>}
        {loading && <div className="admin-loading">Loading…</div>}

        {!loading && !err && (
          rows.length === 0 ? (
            <div className="admin-empty" data-testid="admin-wd-empty">
              {status === 'pending'
                ? "🎉 No withdrawals waiting for review."
                : `No ${status} withdrawals yet.`}
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="admin-wd-table">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>User</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Currency</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th style={{ width: 220 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w) => (
                    <React.Fragment key={w.id}>
                      <tr data-testid={`wd-row-${w.id}`}>
                        <td className="admin-mono admin-small">{formatDate(w.created_at)}</td>
                        <td className="admin-small">
                          <div>{w.user.display_name || '—'}</div>
                          <div className="admin-mono admin-muted">{w.user.email}</div>
                        </td>
                        <td style={{ textAlign: 'right' }} className="admin-mono">
                          <div>{w.usd_amount.toFixed(2)} pts</div>
                          <div className="admin-small admin-muted">≈ {w.estimated_amount} {w.currency}</div>
                        </td>
                        <td>
                          <span className="admin-tag">{w.currency?.toUpperCase()}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className="admin-mono admin-small" title={w.address}>
                              {truncateMiddle(w.address, 10, 6)}
                            </span>
                            <button
                              type="button"
                              className="admin-btn-sm"
                              onClick={() => copyText(w.address, 'Address')}
                              title="Copy full address"
                              data-testid={`wd-copy-address-${w.id}`}
                              style={{ padding: '2px 6px', fontSize: 11 }}
                            >
                              📋
                            </button>
                          </div>
                        </td>
                        <td>
                          <StatusTag status={w.status} />
                          {w.tx_hash && (
                            <div className="admin-mono admin-small admin-muted" style={{ marginTop: 4 }}>
                              tx: {truncateMiddle(w.tx_hash, 8, 6)}
                            </div>
                          )}
                          {w.rejection_reason && (
                            <div className="admin-small admin-muted" style={{ marginTop: 4 }}>
                              {w.rejection_reason}
                            </div>
                          )}
                        </td>
                        <td>
                          {w.status === 'pending' ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="admin-btn-sm ok"
                                disabled={busyId === w.id}
                                onClick={() => openManual(w)}
                                data-testid={`wd-manual-${w.id}`}
                                title="I sent it manually from my own wallet — record the tx hash"
                              >
                                Mark sent ✓
                              </button>
                              <button
                                type="button"
                                className="admin-btn-sm"
                                disabled={busyId === w.id}
                                onClick={() => approve(w)}
                                data-testid={`wd-approve-${w.id}`}
                                title="Auto-payout via NowPayments (requires configured credentials)"
                              >
                                {busyId === w.id ? '…' : 'Auto (NP)'}
                              </button>
                              <button
                                type="button"
                                className="admin-btn-sm warn"
                                disabled={busyId === w.id}
                                onClick={() => openReject(w)}
                                data-testid={`wd-reject-${w.id}`}
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="admin-small admin-muted">—</span>
                          )}
                        </td>
                      </tr>
                      {manualId === w.id && (
                        <tr data-testid={`wd-manual-panel-${w.id}`}>
                          <td colSpan={7}>
                            <div className="admin-inline-panel">
                              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                                Confirm you sent this payout manually
                              </div>
                              <div className="admin-small admin-muted" style={{ marginBottom: 10, lineHeight: 1.45 }}>
                                Send the amount below from your own wallet to the user's address, then paste the tx hash. The user will get a "withdrawal complete" email.
                              </div>

                              {/* Copy amount + address quick-actions */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                                <div className="admin-inline-copy-box">
                                  <div className="admin-small admin-muted">Amount to send</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                    <div className="admin-mono" style={{ fontSize: 13, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {w.estimated_amount} {w.currency?.toUpperCase()}
                                    </div>
                                    <button
                                      type="button"
                                      className="admin-btn-sm"
                                      onClick={() => copyText(String(w.estimated_amount), 'Amount')}
                                      data-testid={`wd-copy-amount-${w.id}`}
                                      title="Copy amount"
                                    >
                                      📋 Copy
                                    </button>
                                  </div>
                                </div>
                                <div className="admin-inline-copy-box">
                                  <div className="admin-small admin-muted">Destination address</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                    <div className="admin-mono" style={{ fontSize: 12.5, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }} title={w.address}>
                                      {truncateMiddle(w.address, 12, 8)}
                                    </div>
                                    <button
                                      type="button"
                                      className="admin-btn-sm"
                                      onClick={() => copyText(w.address, 'Address')}
                                      data-testid={`wd-copy-address-panel-${w.id}`}
                                      title="Copy address"
                                    >
                                      📋 Copy
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <input
                                type="text"
                                className="admin-search"
                                placeholder="Paste the tx hash from Trust / MetaMask / block explorer…"
                                value={manualTxHash}
                                maxLength={200}
                                onChange={(e) => setManualTxHash(e.target.value)}
                                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5 }}
                                data-testid={`wd-manual-tx-${w.id}`}
                              />
                              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                <button
                                  type="button"
                                  className="admin-btn-sm ok"
                                  disabled={busyId === w.id || manualTxHash.trim().length < 6}
                                  onClick={() => confirmManual(w)}
                                  data-testid={`wd-manual-confirm-${w.id}`}
                                >
                                  {busyId === w.id ? 'Recording…' : 'Confirm sent'}
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn-sm"
                                  onClick={cancelManual}
                                  data-testid={`wd-manual-cancel-${w.id}`}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {rejectingId === w.id && (
                        <tr data-testid={`wd-reject-panel-${w.id}`}>
                          <td colSpan={7}>
                            <div className="admin-inline-panel">
                              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                                Reject this withdrawal? {w.usd_amount.toFixed(2)} pts will be refunded.
                              </div>
                              <input
                                type="text"
                                className="admin-search"
                                placeholder="Reason (optional, shown to user)…"
                                value={rejectReason}
                                maxLength={280}
                                onChange={(e) => setRejectReason(e.target.value)}
                                data-testid={`wd-reject-reason-${w.id}`}
                              />
                              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                <button
                                  type="button"
                                  className="admin-btn-sm warn"
                                  disabled={busyId === w.id}
                                  onClick={() => confirmReject(w)}
                                  data-testid={`wd-reject-confirm-${w.id}`}
                                >
                                  {busyId === w.id ? 'Refunding…' : 'Confirm reject + refund'}
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn-sm"
                                  onClick={cancelReject}
                                  data-testid={`wd-reject-cancel-${w.id}`}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AdminLayout>
  );
}

function NpKey({ label, set, value, mono = true }) {
  return (
    <div className="np-key-row">
      <div className="np-key-label">{label}</div>
      <div className={`np-key-value${mono ? ' mono' : ''}`}>
        {set ? (value || '✓') : <span className="np-key-empty">— not set —</span>}
      </div>
    </div>
  );
}

function StatusTag({ status }) {
  const cls = {
    pending: 'admin-tag',
    sent: 'admin-tag tag-ok',
    rejected: 'admin-tag tag-warn',
    failed: 'admin-tag tag-warn',
  }[status] || 'admin-tag';
  return <span className={cls}>{status}</span>;
}

function truncateMiddle(s, left = 8, right = 6) {
  if (!s) return '—';
  if (s.length <= left + right + 3) return s;
  return `${s.slice(0, left)}…${s.slice(-right)}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
